import { StudentResults, LESSON_STATUS, ClessonResults } from '@yandex-int/k-common/typings';
import isEmpty from 'lodash/isEmpty';
import { Spin, Text } from 'platform-components';
import getStudentName from 'platform-components/src/utils/get-student-name';
import {
    withRouterPath,
    InjectedProps as WithRouterPathProps,
} from 'platform-components/src/utils/hocs/with-router-path/with-router-path';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import ym from 'react-yandex-metrika';
import { Action, bindActionCreators, Dispatch } from 'redux';

import LessonContent from 'common.components/lesson-content/lesson-content';
import { LessonViewDetailedViewContent } from 'common.components/lesson-view.detailed-view/__content/detailed-view__content';
import { LessonViewDetailedViewHeader } from 'common.components/lesson-view.detailed-view/__header/detailed-view__header';
import { LessonViewDetailedViewReturnButton } from 'common.components/lesson-view.detailed-view/__return-button/detailed-view__return-button';
import { LessonViewDetailedView } from 'common.components/lesson-view.detailed-view/detailed-view';
import * as lessonViewActions from 'common.components/lesson-view.store/actions';
import * as lessonViewSelectors from 'common.components/lesson-view.store/selectors';
import * as lessonSelectors from 'common.components/lesson/selectors';
import { isLessonCompleted } from 'common.components/lesson/utils';
import * as studentStatSelectors from 'common.components/student-lesson-stat/selectors';
import { LessonProblemWithResult } from 'common.components/student-lesson-stat/typings';
import { Student } from 'common.components/typings';
import * as studentClessonResultActions from 'store/student-clesson-result/actions';
import * as studentClessonResultSelectors from 'store/student-clesson-result/selectors';
import { getTeamIdFromPath } from 'utils/path';

import './student-result-view.scss';

interface StoreProps {
    canShowProblemsContent: boolean;
    clessonId: number | null;
    clessonProblems: null | Array<LessonProblemWithResult>;
    courseId: number | null;
    isEvaluable: boolean;
    isEvaluated: boolean;
    isHeadless: boolean;
    lessonStatus: LESSON_STATUS | null;
    selectedStudent: Student | null;
    shouldLoadCLessonResults: boolean;
    studentId: number | null;
    studentResults: StudentResults | null;
    clessonResults: ClessonResults | null;
    isPointsMode: boolean;
    shouldDisplayAttempts: boolean;
}

interface DispatchProps {
    clearStudentLessonStat: typeof studentClessonResultActions.clear;
    getStudentCLessonResults: typeof studentClessonResultActions.getStudentResultRequest;
    setSelectedStudentId: typeof lessonViewActions.setSelectedStudentId;
}

interface Props extends StoreProps, DispatchProps, WithRouterPathProps {}

export const StudentResultView = (props: Props) => {
    const {
        canShowProblemsContent,
        clearStudentLessonStat,
        clessonId,
        clessonProblems,
        courseId,
        getStudentCLessonResults,
        isEvaluable,
        isEvaluated,
        isHeadless,
        lessonStatus,
        selectedStudent,
        setSelectedStudentId,
        shouldLoadCLessonResults,
        studentId,
        studentResults,
        clessonResults,
        isPointsMode,
        shouldDisplayAttempts,
    } = props;

    useEffect(() => {
        if (!canShowProblemsContent) {
            setSelectedStudentId(null);
        }
    }, [canShowProblemsContent, setSelectedStudentId]);

    useEffect(() => {
        ym('reachGoal', 'result_table_to_student', {
            clessonId,
            student_id: studentId,
        });

        return () => {
            if (lessonStatus && !isLessonCompleted(lessonStatus) && clessonId) {
                clearStudentLessonStat(clessonId);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (clessonId && studentId && shouldLoadCLessonResults) {
            getStudentCLessonResults(clessonId, studentId);
        }
    }, [clessonId, getStudentCLessonResults, studentId, shouldLoadCLessonResults]);

    if (!selectedStudent || !clessonProblems || !courseId) {
        return null;
    }

    return (
        <LessonViewDetailedView mix="student-result-view">
            <LessonViewDetailedViewHeader isHeadless={isHeadless}>
                <LessonViewDetailedViewReturnButton
                    mix="student-result-view__return-button"
                    onClick={() => setSelectedStudentId(null)}
                    textId="resultView.goToResults"
                />
            </LessonViewDetailedViewHeader>
            <LessonViewDetailedViewContent mix="student-result-view__content">
                <div className="qa_student-name">
                    <Text size={Text.SIZE.XXXL} weight={Text.WEIGHT.M}>
                        {getStudentName(selectedStudent.firstName, selectedStudent.lastName)}
                    </Text>
                </div>
                {isEmpty(studentResults) ? (
                    <Spin center />
                ) : (
                    <LessonContent
                        correctAnswersAvailable={isEvaluated}
                        fullScreen={false}
                        isEvaluable={isEvaluable}
                        isEvaluated={isEvaluated}
                        isPointsMode={isPointsMode}
                        lessonProblemResults={clessonResults?.problems || {}}
                        mix="student-result-view__problems"
                        problems={clessonProblems}
                        shouldDisplayAttempts={shouldDisplayAttempts}
                        studentId={studentId}
                        studentResults={studentResults}
                    />
                )}
            </LessonViewDetailedViewContent>
        </LessonViewDetailedView>
    );
};

StudentResultView.contextTypes = {
    // eslint-disable-next-line react/forbid-prop-types
    staticUrls: PropTypes.object.isRequired,
};

const mapStateToProps = (storeState: unknown): StoreProps => {
    const classId = getTeamIdFromPath();
    const studentId = lessonViewSelectors.getSelectedStudentId(storeState);
    const clessonId = lessonSelectors.getCLessonId(storeState);

    return {
        canShowProblemsContent: lessonSelectors.getCanShowProblemsContent(storeState),
        clessonId,
        // @ts-ignore
        clessonProblems: studentStatSelectors.getLessonProblemsWithResult(storeState, { clessonId, studentId }),
        courseId: lessonSelectors.getLessonCourseId(storeState),
        isEvaluable: lessonSelectors.getIsLessonEvaluable(storeState),
        isEvaluated: lessonSelectors.getIsLessonEvaluated(storeState),
        isHeadless: lessonViewSelectors.getIsHeadless(storeState),
        isPointsMode: lessonSelectors.getIsPointsMode(storeState),
        shouldDisplayAttempts: lessonSelectors.getShouldDisplayAttempts(storeState),
        lessonStatus: lessonSelectors.getLessonStatus(storeState),
        selectedStudent: lessonViewSelectors.getSelectedStudent(storeState, { classId }),
        shouldLoadCLessonResults: studentClessonResultSelectors.getShouldLoadCLessonResults(storeState, {
            clessonId,
            studentId,
        }),
        studentId,
        studentResults: studentStatSelectors.getResults(storeState, { studentId }),
        clessonResults: lessonSelectors.getClessonResults(storeState),
    };
};

const mapDispatchToProps = (dispatch: Dispatch<Action>): DispatchProps => {
    return bindActionCreators(
        {
            clearStudentLessonStat: studentClessonResultActions.clear,
            getStudentCLessonResults: studentClessonResultActions.getStudentResultRequest,
            setSelectedStudentId: lessonViewActions.setSelectedStudentId,
        },
        dispatch
    );
};

const component = withRouterPath(connect(mapStateToProps, mapDispatchToProps)(StudentResultView));
component.displayName = 'StudentResultView';

export default component;
